import { useState } from "react";
import { Trash2 } from "lucide-react";
import { useDashboardCards } from "@/hooks/useCashChat";
import { AssistantChart } from "./AssistantChart";
import { MintiMascot } from "./MintiMascot";

/**
 * Charts the user pinned from the assistant. Empty until they pin one, so
 * it stays out of the way for anyone not using the assistant.
 */
export function MyDashboardSection() {
  const { cards, isLoading, error, removeCard } = useDashboardCards();
  const [removingId, setRemovingId] = useState<number | null>(null);

  if (isLoading) {
    return (
      <div className="grid gap-4 sm:grid-cols-2">
        {[0, 1].map((key) => (
          <div
            key={key}
            className="h-64 animate-pulse rounded-xl border border-[#E5E7EB] bg-[#F3F4F6]"
          />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div
        role="alert"
        className="rounded-xl border border-[#FECACA] bg-[#FEF2F2] px-4 py-3 text-sm text-[#DC2626]"
      >
        {error}
      </div>
    );
  }

  if (cards.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-[#E5E7EB] bg-[#FAFAFA] px-6 py-10 text-center">
        <span className="flex h-11 w-11 items-center justify-center overflow-hidden rounded-2xl bg-[#E4F5EA]">
          <MintiMascot className="h-11 w-11" />
        </span>
        <p className="mt-3 text-sm font-medium text-[#111827]">
          No pinned charts yet
        </p>
        <p className="mt-1 max-w-sm text-sm text-[#6B7280]">
          Open <span className="font-medium">Ask Minti</span>, request a chart
          — &ldquo;chart collections by month&rdquo; — then pin it here.
        </p>
      </div>
    );
  }

  async function handleRemove(cardId: number) {
    setRemovingId(cardId);
    try {
      await removeCard(cardId);
    } finally {
      setRemovingId(null);
    }
  }

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      {cards.map((card) => (
        <article
          key={card.id}
          aria-label={card.title}
          className="rounded-xl border border-[#E5E7EB] bg-white p-4 shadow-sm"
        >
          <header className="mb-3 flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h3 className="truncate text-sm font-semibold text-[#111827]">
                {card.title}
              </h3>
              {card.sourcePrompt ? (
                <p className="mt-0.5 truncate text-xs text-[#898781]">
                  Asked: {card.sourcePrompt}
                </p>
              ) : null}
            </div>
            <button
              type="button"
              onClick={() => void handleRemove(card.id)}
              disabled={removingId === card.id}
              aria-label={`Remove ${card.title}`}
              title="Remove from dashboard"
              className="shrink-0 rounded-md p-1.5 text-slate-400 transition hover:bg-slate-50 hover:text-red-600 disabled:opacity-50"
            >
              <Trash2 className="h-3.5 w-3.5" aria-hidden />
            </button>
          </header>
          <div className="h-64 w-full">
            <AssistantChart spec={card.spec} />
          </div>
        </article>
      ))}
    </div>
  );
}

